import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import SlotConflictPanel from "../../components/SlotConflictPanel";
import { getDirectShopCataloguePath } from "../../utils/shopUrls";
import { keepSlotConflictTime } from "../../utils/slotConflictApi";
import { clearAllBookingStorage } from "../../utils/bookingDraft";
import "./BookingConfirmation.css";

const resolveProductImageUrl = (product) => {
  const firstImage =
    (Array.isArray(product?.images) && product.images.length > 0
      ? product.images[0]
      : null) ||
    product?.imageUrl ||
    null;

  const rawImageUrl =
    typeof firstImage === "string"
      ? firstImage
      : firstImage?.url || firstImage?.imageUrl || "";

  if (!rawImageUrl) return "";
  if (/^https?:\/\//i.test(rawImageUrl) || rawImageUrl.startsWith("data:")) {
    return rawImageUrl;
  }

  const baseUrl = String(process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/+$/,
    "",
  );
  const normalizedPath = rawImageUrl.startsWith("/")
    ? rawImageUrl
    : `/${rawImageUrl}`;
  return baseUrl ? `${baseUrl}${normalizedPath}` : rawImageUrl;
};

/**
 * THE SERVICE, FOR WHATEVER KIND OF BOOKING ARRIVED — including the ones with no single service.
 *
 * Reported by the user, 2026-10-14: a GROUP booking confirmed with **"SERVICE: N/A"**, because a group has no
 * `serviceName` on the document it was routed with — each ATTENDEE has their own service. The server's own
 * `/confirm` payload builds a summary (`getBookingServiceSummary`), but this page is also reached through
 * `location.state` from the booking flow, where the document is whatever the booking request returned.
 *
 * So the label is derived from everything the booking can carry, in order of certainty: the summary the server
 * built, the single service, the bundle, then the members' own services. `"Service"` — never `"N/A"` — is the
 * floor, because "N/A" reads as a broken booking rather than as a summary we did not compute.
 */
function serviceLabelFor(booking) {
  const direct = booking?.serviceName || booking?.serviceId?.name;
  if (direct) return direct;

  const bundle = Array.isArray(booking?.serviceBundle)
    ? booking.serviceBundle
        .map((entry) => entry?.name || entry?.serviceName)
        .filter(Boolean)
    : [];
  if (bundle.length > 0) return [...new Set(bundle)].join(" + ");

  const members = Array.isArray(booking?.groupMembers)
    ? booking.groupMembers
        .map((member) => member?.serviceName)
        .filter(Boolean)
    : [];
  if (members.length > 0) return [...new Set(members)].join(" + ");

  return "Service";
}

const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const isTestBooking = searchParams.get("test") === "true";

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [featuredProducts, setFeaturedProducts] = useState([]);
  /**
   * A PACKAGE BOOKING ARRIVES HERE TOO (user, 2026-10-11: *"do the confirmation page matching the card page
   * as well"*). It has no Stripe session and — for a guest — no booking to fetch, because
   * `GET /api/bookings/:id` needs a session. So the server hands the booking DOCUMENT in the response and the
   * page renders it exactly as it renders a card booking's, with the package facts alongside.
   *
   * `packageMeta` carries the things only a package knows and the booking document cannot: how many sessions
   * the claim spent, how many are left, and who was served.
   */
  const [packageMeta, setPackageMeta] = useState(null);
  // Part B: slot conflict acknowledgement state.
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState("");

  // Guests have no account to acknowledge from, so they are offered the email
  // route instead of a button that would 401.
  const hasAuthToken = Boolean(localStorage.getItem("ogestyle_token"));

  const handleKeepSlotConflictTime = async (targetBooking) => {
    const target = targetBooking || booking;
    const bookingRef = target?._id || target?.bookingId;

    if (!bookingRef) return;

    setKeeping(true);
    setKeepError("");

    try {
      const updatedBooking = await keepSlotConflictTime(bookingRef);

      setBooking((previous) => ({
        ...previous,
        ...updatedBooking,
        slotConflict: updatedBooking?.slotConflict || previous?.slotConflict,
      }));
    } catch (err) {
      setKeepError(err.message || "Unable to keep this time");
    } finally {
      setKeeping(false);
    }
  };

  useEffect(() => {
    /**
     * ── A CONFIRMED BOOKING LEAVES NO DRAFT BEHIND (reported 2026-10-27) ──────────────────────────────
     *
     * *"after a package is bought, booking is returned to and the booking is done, the session is still storing
     * the information … it MIGHT be for a different user"*. A completed booking is not an attempt in progress, so
     * every booking draft and purchase marker goes — including a second attempt started for another professional
     * in another tab, which has the same name, email and phone in it.
     *
     * Safe here and NOT on the package-purchase page: that one sends the customer BACK to the booking, where the
     * draft is the whole point.
     */
    clearAllBookingStorage();
  }, []);

  useEffect(() => {
    // ── A PACKAGE BOOKING IS ALREADY COMPLETE, SO THERE IS NOTHING TO VERIFY ───────────────────────
    //
    // No Stripe session to confirm and no booking to fetch: the session has already been spent server-side
    // and the response IS the confirmation. Checked FIRST, because the two paths below would both fail for it
    // ("Invalid confirmation link", or a 401 for a guest) and show the customer an error over a booking that
    // succeeded.
    // THE META IS THE SIGNAL, NOT THE BOOKING. `packageMeta` is always sent with a package booking, so a
    // response that somehow lacked the document still lands here and renders the package facts instead of
    // falling through to "Invalid confirmation link" — an error over an appointment that succeeded is the
    // worst outcome this page can produce. Every field below is read defensively for that reason.
    if (location.state?.packageMeta) {
      setBooking(location.state.packageBooking || null);
      setPackageMeta(location.state.packageMeta);
      setLoading(false);
      return;
    }

    // Handle test bookings from preview mode (passed via navigation state)
    if (isTestBooking && location.state?.booking) {
      setBooking(location.state.booking);
      setLoading(false);
      return;
    }

    // Handle test bookings by fetching from API
    if (isTestBooking && bookingId) {
      fetchTestBooking();
      return;
    }

    // Normal Stripe flow - only session_id needed (booking created in webhook)
    if (sessionId) {
      verifyPaymentAndFetchBooking();
    } else {
      setError("Invalid confirmation link");
      setLoading(false);
    }
  }, [sessionId, bookingId, isTestBooking, location.state]);

  const fetchTestBooking = async () => {
    try {
      // Fetch test booking details directly (no payment verification needed)
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${bookingId}`,
      );

      if (!response.ok) {
        throw new Error("Unable to fetch booking details");
      }

      const data = await response.json();
      setBooking(data.booking || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyPaymentAndFetchBooking = async () => {
    try {
      // Verify payment and get booking details (booking created in webhook)
      // No booking_id needed - endpoint finds booking by session_id
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/confirm?session_id=${sessionId}`,
      );

      if (!response.ok) {
        let errorMessage = "Unable to verify payment";
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // Keep generic fallback when response body is unavailable
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setBooking(data.booking);

      // Fetch featured products from the shop for upsell
      if (data.booking?.shopId) {
        fetchFeaturedProducts(data.booking.shopId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedProducts = async (shopId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopId}?limit=3&featured=true`,
      );

      if (response.ok) {
        const data = await response.json();
        setFeaturedProducts(data.products || []);
      }
    } catch (err) {
      // Don't show error to user - this is optional upsell
    }
  };

  const handleProductClick = (product) => {
    // Navigate to individual product page instead of opening modal
    navigate(`/product/${product._id}`);
  };

  const formatDate = (dateString) => {
    // Handle both full ISO dates and YYYY-MM-DD format
    let date;
    if (dateString.includes("T")) {
      // Full ISO date with time - parse as UTC then extract date components
      date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      date = new Date(year, month, day);
    } else {
      // YYYY-MM-DD format - parse as local
      const [year, month, day] = dateString.split("-").map(Number);
      date = new Date(year, month - 1, day);
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getProductVariantSummary = (product = {}) => {
    const attributes = Array.isArray(product.variantAttributes)
      ? product.variantAttributes
      : [];

    if (attributes.length > 0) {
      return attributes
        .map((attribute) => {
          const type = String(
            attribute?.dimensionName ||
              attribute?.dimensionKey ||
              attribute?.name ||
              attribute?.type ||
              "Variant",
          ).trim();
          const value = String(attribute?.label || attribute?.value || "").trim();
          return value ? `${type}: ${value}` : type;
        })
        .filter(Boolean)
        .join(" • ");
    }

    const fallbackType = String(
      product.variantType || product.variantLabel || product.variantName || "",
    ).trim();
    const fallbackValue = String(product.variantValue || "").trim();

    if (fallbackType && fallbackValue) {
      return `${fallbackType}: ${fallbackValue}`;
    }

    return fallbackType;
  };

  if (loading) {
    return (
      <div className="confirmation-container">
        <div className="confirmation-loading">
          <div className="spinner"></div>
          <p>Confirming your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="confirmation-container">
        <div className="confirmation-error">
          <div className="error-icon">❌</div>
          <h1>Booking Confirmation Failed</h1>
          <p>{error}</p>
          <button onClick={() => navigate("/shops")} className="btn-primary">
            Browse Shops
          </button>
        </div>
      </div>
    );
  }

  const confirmationServiceTotal =
    Number(booking?.servicePriceAtBooking || 0) +
    Number(
      (booking?.groupMembers || []).reduce(
        (sum, member) => sum + Number(member?.servicePriceAtBooking || 0),
        0,
      ),
    );

  /**
   * TOTAL appointment length: the primary customer's booked window (their service plus
   * add-ons) AND every group member's own service.
   *
   * `booking.duration` is only the primary's window - the members are chained AFTER it,
   * so showing it alone under-reports a group appointment by the members' time.
   */
  const confirmationMemberMinutes = (booking?.groupMembers || []).reduce(
    (sum, member) =>
      sum +
      (Number(member?.serviceDuration) || Number(member?.duration) || 0),
    0,
  );
  const confirmationDurationMinutes =
    (Number(booking?.serviceBundleDuration) || Number(booking?.duration) || 0) +
    confirmationMemberMinutes;
  const confirmationProductsTotal = Number(booking?.productsTotalPrice || 0);
  const confirmationTaxAmount = Number(booking?.tax || 0);
  const stripeTaxRate = Number(booking?.taxRate || 0);
  /**
   * ── WHAT THE CARD ACTUALLY TOOK, AND WHAT THE PACKAGE PAID FOR (split payment, 2026-10-27) ──────────
   *
   * The rows above are the VALUE DELIVERED. On a split booking the package paid for part of it, so summing them
   * told the customer $94.99 while Stripe had taken $67.20 — with the tax that WAS charged showing as $0.00.
   *
   * `amountPaid` is what Stripe collected for this checkout, written by the webhook from the session along with
   * `tax`. So the figure at the bottom is read where it exists, never recomputed: a component sum cannot know what
   * a package covered, and it cannot know Stripe's tax. Older bookings without it fall back to the sum, which is
   * what this page has always done.
   */
  /**
   * ── WHAT THE PACKAGE ACTUALLY PAID FOR, ON THIS PAGE'S OWN ROWS (2026-10-27) ───────────────────────
   *
   * `coveredServiceValue` is the PRIMARY's covered service only: a covered party member's value sits on their own
   * sibling booking, with its own link and its own tranche. But the "Service Total" row above sums the WHOLE party,
   * so the credit has to be derived the same way — total booked service minus what the card was charged for
   * services — or the column does not add up and "Total Paid" reads as if the package credit were ignored.
   *
   * IT MUST ONLY APPEAR ON A PACKAGE BOOKING. `paymentSplit` is written for EVERY confirmed booking (the confirm
   * route and the webhook both store one), so `booking.splitPayment` being truthy says nothing — on an ordinary
   * card booking this row read "Covered by your package −$120.00" and the total then subtracted the whole service.
   * The package LINK is the fact, exactly as `isPackageSessionBooking` decides it on the server.
   */
  const bookingUsesPackage = Boolean(
    booking?.packagePurchaseId ||
      booking?.usePackageCredit === true ||
      Number(booking?.splitPayment?.coveredServiceValue) > 0,
  );
  const confirmationPackageCredit = bookingUsesPackage
    ? Math.max(
        0,
        confirmationServiceTotal -
          (Number(booking.splitPayment?.cardServiceValue) || 0) -
          (Number(booking.splitPayment?.cardMemberServiceValue) || 0),
      )
    : 0;
  const confirmationComponentTotal =
    confirmationServiceTotal +
    confirmationProductsTotal +
    Number(booking?.travelFee || 0) +
    Number(booking?.tipAmount || 0) +
    confirmationTaxAmount;
  /**
   * TOTAL PAID IS THE CARD'S MONEY, NOT THE VALUE DELIVERED. On a package booking the package paid for part of the
   * appointment, so the figure at the bottom must be what was actually charged to the card — the components minus
   * what the package covered — and on a split booking that is exactly `amountPaid`.
   */
  const confirmationTotalPaid =
    Number(booking?.amountPaid) > 0
      ? Number(booking.amountPaid)
      : Math.max(0, confirmationComponentTotal - confirmationPackageCredit);
  // A package booking took NO card payment, and the figures above are the service's VALUE, not an amount
  // charged. Rendering them under "Total Paid" would tell the customer they were billed for something their
  // package covered.
  const isPackageBooking = Boolean(packageMeta);
  const shopAllProductsPath =
    getDirectShopCataloguePath(featuredProducts?.[0]?.shopId) ||
    getDirectShopCataloguePath(booking?.shopId);

  return (
    <>
      <CustomerHeader />
      <div className="confirmation-container">
        <div className="confirmation-success">
          {/* Success Header */}
          <div
            className="confirmation-success-header"
            style={
              isTestBooking || booking?.isTestBooking
                ? {
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }
                : {}
            }
          >
            <div className="success-icon">
              {isTestBooking || booking?.isTestBooking ? "🧪" : "✅"}
            </div>
            <h1>
              {isTestBooking || booking?.isTestBooking
                ? "Test Booking Created!"
                : booking?.slotConflict?.outcome &&
                    booking?.slotConflict?.resolution !== "refunded"
                  ? booking.slotConflict.outcome === "parked"
                    ? "We are sorting out your appointment"
                    : "We have held a new time for you"
                  : booking?.groupMembers?.length > 0
                    ? "Group Booking Confirmed!"
                    : "Booking Confirmed!"}
            </h1>
            <p className="success-message">
              {isTestBooking || booking?.isTestBooking
                ? "Your test appointment has been created. Check your dashboard to see it!"
                : booking?.slotConflict?.outcome &&
                    booking?.slotConflict?.resolution !== "refunded"
                  ? "Your payment went through, but the time you picked was taken first. Here is what happens next."
                  : booking?.groupMembers?.length > 0
                    ? `Your group appointment for ${1 + booking.groupMembers.length} people has been successfully booked and paid for.`
                    : "Your appointment has been successfully booked and paid for."}
            </p>
          </div>

          {/* Part B: the time the customer paid for was taken mid-payment. Shown
              right here, on the page they land on straight after paying. */}
          {booking && (
            <SlotConflictPanel
              booking={booking}
              keeping={keeping}
              keepError={keepError}
              onKeep={hasAuthToken ? handleKeepSlotConflictTime : undefined}
            />
          )}

          {booking && (
            <div className="booking-details">
              {/* Test Booking Notice */}
              {(isTestBooking || booking?.isTestBooking) && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <span style={{ fontSize: "24px" }}>⚠️</span>
                  <div>
                    <strong style={{ color: "#92400e" }}>Test Mode</strong>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: "14px",
                        color: "#a16207",
                      }}
                    >
                      This is a test booking. No payment was charged.
                    </p>
                  </div>
                </div>
              )}

              {/* Appointment Details */}
              <div className="detail-section">
                <h2>📅 Appointment Details</h2>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Service</span>
                    <span className="value">
                      {serviceLabelFor(booking)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Professional</span>
                    <span className="value">
                      {booking.staffName || booking.customerName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Date</span>
                    <span className="value">
                      {formatDate(booking.appointmentDate)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Time</span>
                    <span className="value">
                      {formatTime(booking.startTime)} -{" "}
                      {formatTime(booking.endTime)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="detail-section">
                <h2>👤 Contact Information</h2>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Name</span>
                    <span className="value">{booking.customerName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Email</span>
                    <span className="value">{booking.customerEmail}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone</span>
                    <span className="value">{booking.customerPhone}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Duration</span>
                    <span className="value">{confirmationDurationMinutes} minutes</span>
                  </div>
                </div>
              </div>

              {booking.groupMembers && booking.groupMembers.length > 0 && (
                <div className="detail-section">
                  <h2>👥 Group Members ({booking.groupMembers.length})</h2>
                  <div className="group-members-list">
                    {booking.groupMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="group-member-item"
                        style={{
                          padding: "12px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          marginBottom: "8px",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span style={{ fontWeight: "600", color: "#111827" }}>
                            {member.name}
                          </span>
                          <span style={{ fontWeight: "600", color: "#1a659e" }}>
                            ${member.servicePriceAtBooking?.toFixed(2)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          {member.serviceName}
                        </div>
                        <div style={{ fontSize: "13px", color: "#9ca3af" }}>
                          {member.startTime &&
                            member.endTime &&
                            `${formatTime(member.startTime)} - ${formatTime(member.endTime)}`}
                          {member.duration && ` (${member.duration} min)`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {booking.products && booking.products.length > 0 && (
                <div className="detail-section">
                  <h2>Products to Pickup</h2>
                  <div className="products-list">
                    {booking.products.map((product, idx) => (
                      <div key={idx} className="product-item">
                        <span className="product-name">
                          {product.productName}
                          {getProductVariantSummary(product)
                            ? ` • ${getProductVariantSummary(product)}`
                            : ""}
                        </span>
                        <span className="product-quantity">
                          Qty: {product.quantity}
                        </span>
                        <span className="product-price">
                          $
                          {(product.priceAtBooking * product.quantity).toFixed(
                            2,
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isPackageBooking ? (
                <div className="detail-section payment-summary">
                  <h2>🎁 Paid with your package</h2>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Sessions used by this booking</span>
                      <span>{Number(packageMeta.sessionsUsed) || 1}</span>
                    </div>
                    {typeof packageMeta.sessionsRemaining === "number" && (
                      <div className="summary-item">
                        <span>Sessions remaining on your package</span>
                        <span>{packageMeta.sessionsRemaining}</span>
                      </div>
                    )}
                    <div className="summary-item total">
                      <span>Charged to your card</span>
                      <span>$0.00</span>
                    </div>
                  </div>
                  {/* ── TEXT ON A DARK PANEL MUST SAY ITS OWN COLOUR ─────────────────────────────────
                      Reported with a screenshot, 2026-10-12: "some texts highlighted are not showing".
                      They were showing — in the panel's INHERITED colour, which `.payment-summary` sets to a
                      dark slate for a light background. On this block's own dark gradient
                      (`#1e293b → #334155`) that is very nearly invisible.

                      The rows above are legible only because `.summary-item` forces `#4ade80 !important`. The
                      colours below are therefore EXPLICIT rather than inherited: a block that relies on the
                      cascade is a block that disappears the moment the theme it was written against changes —
                      which is exactly what happened here. */}
                  {Array.isArray(packageMeta.people) && packageMeta.people.length > 1 && (
                    <div style={{ marginTop: "14px" }}>
                      <strong style={{ color: "#ffffff", fontSize: "14px" }}>
                        Who this booking covers:
                      </strong>
                      <ul
                        style={{
                          margin: "6px 0 0 0",
                          paddingLeft: "20px",
                          color: "#e2e8f0",
                          fontSize: "14px",
                          lineHeight: 1.6,
                        }}
                      >
                        {packageMeta.people.map((person, index) => (
                          <li key={index}>
                            {person.name ? `${person.name} — ` : ""}
                            {person.serviceName || "Service"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* TWO SENTENCES, TWO LINES. They ran together as one paragraph — "…package credit Tipping is
                      settled…" — because the server's message ends without punctuation and the second sentence
                      was appended to it with a space. */}
                  <p
                    style={{
                      margin: "14px 0 0 0",
                      fontSize: "13px",
                      color: "#cbd5e1",
                      lineHeight: 1.6,
                    }}
                  >
                    {(() => {
                      const message =
                        packageMeta.message ||
                        "This booking was paid with your package — no card payment was taken.";
                      // A full stop the server did not send, so the next sentence does not read as part of it.
                      return /[.!?]$/.test(message.trim()) ? message : `${message}.`;
                    })()}
                  </p>
                  <p
                    style={{
                      margin: "6px 0 0 0",
                      fontSize: "13px",
                      color: "#cbd5e1",
                      lineHeight: 1.6,
                    }}
                  >
                    Tipping is settled directly with your professional.
                  </p>
                  {/* THE WAY BACK, WHICH THE OLD INLINE PANEL ALSO LACKED. It reloads the booking screen
                      rather than restoring it, so the package figures are re-read: a RESTORED page is what
                      showed pre-booking session counts on 2026-10-11. */}
                  {location.state?.returnTo && (
                    <button
                      type="button"
                      className="btn-primary"
                      style={{ marginTop: "16px" }}
                      onClick={() => {
                        window.location.href = location.state.returnTo;
                      }}
                    >
                      Book another appointment
                    </button>
                  )}
                </div>
              ) : (
              <div className="detail-section payment-summary">
                <h2>💳 Payment Summary</h2>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span>
                      Service Total
                      {booking.groupMembers && booking.groupMembers.length > 0
                        ? ` (${1 + booking.groupMembers.length} people)`
                        : ""}
                    </span>
                    <span>${confirmationServiceTotal.toFixed(2)}</span>
                  </div>
                  {/* ── WHAT THE PACKAGE PAID FOR (split payment, 2026-10-27) ──────────────────────────
                      Without this row a split booking read as "Service Total $60 … Total Paid $94.99", when the
                      card had taken $67.20 — because the rows above are the VALUE delivered and the package paid
                      for part of it. The credit is stated, and the figure at the bottom is what was charged. */}
                  {confirmationPackageCredit > 0 && (
                    <div className="summary-item">
                      <span style={{ color: "#059669" }}>Covered by your package</span>
                      <span style={{ color: "#059669" }}>−${confirmationPackageCredit.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-item">
                    <span>Products Total</span>
                    <span>${confirmationProductsTotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Travel Fee</span>
                    <span>${Number(booking.travelFee || 0).toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span>
                      Tip{typeof booking.tipPercentage === 'number' && booking.tipPercentage > 0 ? ` (${booking.tipPercentage}%)` : ''}
                    </span>
                    <span>${Number(booking.tipAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="summary-item">
                    <span>
                      Sales Tax
                    </span>
                    <span>${confirmationTaxAmount.toFixed(2)}</span>
                  </div>
                  <div className="summary-item total">
                    <span>
                      {confirmationPackageCredit > 0
                        ? "Total Paid by card"
                        : "Total Paid"}
                    </span>
                    <span>${confirmationTotalPaid.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              )}

              <div className="confirmation-note">
                <p>
                  📧 A confirmation email has been sent to{" "}
                  <strong>{booking.customerEmail}</strong>
                </p>
                <p>
                  📱 You'll receive a reminder 24 hours before your appointment
                </p>
                <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "8px" }}>
                  💡 If you don't see the confirmation email in your inbox, please check your spam or promotions folder.
                </p>
              </div>
            </div>
          )}

          {/* Product Upsell Section */}
          {featuredProducts.length > 0 && (
            <div className="product-upsell-section">
              <div className="upsell-header">
                <h2>🛍️ While you're here...</h2>
                <p>
                  Browse our products and pick them up during your appointment!
                </p>
              </div>

              <div className="upsell-products-grid">
                {featuredProducts.map((product) => (
                  <div
                    key={product._id}
                    className="upsell-product-card"
                    onClick={() => handleProductClick(product)}
                    style={{ cursor: "pointer" }}
                  >
                    {resolveProductImageUrl(product) && (
                      <div className="upsell-product-image">
                        <img
                          src={resolveProductImageUrl(product)}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src =
                              "https://placehold.co/150x150/e9ecef/666/png?text=Product";
                          }}
                        />
                      </div>
                    )}
                    <div className="upsell-product-info">
                      <h3>{product.name}</h3>
                      {product.brand && (
                        <p className="product-brand">{product.brand}</p>
                      )}
                      <div className="product-price-row">
                        <span className="product-price">
                          ${product.price.toFixed(2)}
                        </span>
                        {product.stock > 0 ? (
                          <span className="stock-badge in-stock">In Stock</span>
                        ) : (
                          <span className="stock-badge out-of-stock">
                            Out of Stock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate(shopAllProductsPath || "/products")}
                className="btn-secondary"
                style={{ marginTop: "20px", width: "100%", maxWidth: "300px" }}
              >
                Shop All Products
              </button>
            </div>
          )}

          <div className="confirmation-actions">
            <button
              onClick={() => navigate("/shops")}
              className="btn-secondary"
            >
              Book Another Appointment
            </button>
            <button
              onClick={() => navigate("/customer/dashboard/appointments")}
              className="btn-primary"
            >
              View My Appointments
            </button>
          </div>
        </div>
      </div>

      <CustomerFooter />
    </>
  );
};

export default BookingConfirmation;
